"use client"

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Sparkles, Loader2, AlertCircle, Clock } from "lucide-react"
import type { SentimentInsightResponse } from "@/lib/ai/sentiment-types"
import { EMOTION_LABELS_CS, TRIGGER_LABELS_CS } from "@/lib/ai/sentiment-types"

interface SentimentInsightCardProps {
  entryId: string
}

// Runtime validation helper
function isValidSentimentResponse(data: any): data is SentimentInsightResponse {
  if (!data || typeof data !== 'object') return false
  if (typeof data.insight !== 'string') return false
  if (typeof data.pending !== 'boolean') return false
  if (data.sentiment !== null && typeof data.sentiment !== 'object') return false
  return true
}

export function SentimentInsightCard({ entryId }: SentimentInsightCardProps) {
  const [data, setData] = useState<SentimentInsightResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    let mounted = true
    let timeoutId: NodeJS.Timeout | null = null

    async function fetchInsight() {
      try {
        const response = await fetch("/api/ai/sentiment-insight", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entryId }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          if (response.status === 429) {
            throw new Error("Příliš mnoho požadavků. Zkus to za chvíli.")
          }
          throw new Error(errorText || "Nepodařilo se načíst analýzu")
        }

        const result = await response.json()

        if (!mounted) return

        // Validate response structure
        if (!isValidSentimentResponse(result)) {
          throw new Error("Neplatná odpověď ze serveru")
        }

        if (result.pending && retryCount < 5) {
          // Sentiment analysis still in progress, retry after delay
          setIsPending(true)
          timeoutId = setTimeout(() => {
            setRetryCount((c) => c + 1)
          }, 1500) // Retry every 1.5 seconds
        } else if (result.pending) {
          // Exceeded retry limit
          setError("Analýza stále probíhá. Zkus obnovit stránku za chvíli.")
          setLoading(false)
          setIsPending(false)
        } else {
          setData(result)
          setLoading(false)
          setIsPending(false)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Nepodařilo se načíst analýzu")
          setLoading(false)
          setIsPending(false)
        }
      }
    }

    fetchInsight()

    return () => {
      mounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [entryId, retryCount])

  // Error state - show user-friendly error message
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
      >
        <Card className="bg-muted/30 border-muted">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-xs text-muted-foreground">{error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs px-2"
                  onClick={() => {
                    setError(null)
                    setLoading(true)
                    setRetryCount(0)
                  }}
                >
                  Zkusit znovu
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // Loading state - show pending analysis if applicable
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
      >
        <Card className="bg-muted/30 border-muted">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              {isPending ? (
                <>
                  <Clock className="h-4 w-4" />
                  <span className="text-xs">Analýza stále probíhá ({retryCount + 1}/5)...</span>
                </>
              ) : (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Analyzuji poznámku...</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // No insight available (no note or analysis failed)
  if (!data || !data.sentiment || !data.insight) {
    return null
  }

  const { insight, sentiment } = data

  // Get emotion and trigger badges
  const emotionBadges = sentiment.emotions.slice(0, 2).map((e) => ({
    key: e,
    label: EMOTION_LABELS_CS[e],
    type: "emotion" as const,
  }))

  const triggerBadges = sentiment.triggers
    .filter((t) => t !== "unknown")
    .slice(0, 2)
    .map((t) => ({
      key: t,
      label: TRIGGER_LABELS_CS[t],
      type: "trigger" as const,
    }))

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-4 pb-4 px-4">
            <div className="flex items-start gap-2.5">
              <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-xs sm:text-sm leading-relaxed text-pretty">
                  {insight}
                </p>
                {(emotionBadges.length > 0 || triggerBadges.length > 0) && (
                  <div className="flex flex-wrap gap-1.5">
                    {emotionBadges.map((badge) => (
                      <Badge
                        key={badge.key}
                        variant="secondary"
                        className="text-xs py-0 px-2 bg-primary/10 hover:bg-primary/15"
                      >
                        {badge.label}
                      </Badge>
                    ))}
                    {triggerBadges.map((badge) => (
                      <Badge
                        key={badge.key}
                        variant="outline"
                        className="text-xs py-0 px-2"
                      >
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  )
}
